import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from "react";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { useHistory, useLocation } from "react-router";
import { LoadingIndicator, PolyIcon } from "elements";
import {
  Link,
  QParam,
  SelectedTenant,
  useQueryParam,
  useQueryParamString
} from "polyrouter";
import { Tenant, useTenantsListQuery } from "generated/graphql";
import { SetUserMetaFn, useUserMeta } from "utilities/userMeta";
import "./AdminMenu.scss";
import VisibilitySensor from "react-visibility-sensor";
import { accountLimit } from "Constants";
import { usePolicyFilterValue } from "elements/Filter/PolicyFilter";
import { removeBetaNotificationFromLocalStorage } from "utilities/betaNotificationUtils";
import { useTenantsControlState } from "routes/Admin/Accounts/TenantsControlState";
import { PayloadAC } from "typesafe-actions";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { usePermissions } from "../../rbac";
import { trackMenuSelection } from "monitoring/trackMenuSelection";

export type TenantItem = Pick<Tenant, "id" | "name">;
const MAX_TENANT_ITEMS = 6;

export interface TenantSwitcherProps {
  tenants: Array<TenantItem>;
  namePrefix: string | undefined;
  updateNamePrefix: PayloadAC<"UpdateNamePrefix", string | undefined>;
  incrementFrom: PayloadAC<"IncrementFrom", number>;
  loading: boolean;
}

export const unknownNamePlaceholder = (t: (key: LocalKeys) => string) => {
  return t("UNKNOWN_TENANT");
};

export const TenantSwitcher: React.FC = () => {
  const [
    { from, namePrefix, previousNamePrefix },
    actions
  ] = useTenantsControlState();
  const {
    data: tenantsListData,
    fetching: tenantsListFetching
  } = useTenantsListQuery({
    variables: {
      namePrefix: previousNamePrefix,
      from,
      limit: accountLimit
    },
    requestPolicy: "cache-and-network"
  });
  const [accounts, setAccounts] = useState(
    {} as { [tenantId: string]: TenantItem }
  );
  const queriedAccounts = (() => {
    const queriedTenants = tenantsListData?.tenants ?? [];
    const filteredAccounts = Object.values(
      queriedTenants.map(({ id }) => accounts[id]).filter(a => a)
    );
    return filteredAccounts;
  })();

  useEffect(() => {
    if (!tenantsListFetching && previousNamePrefix !== namePrefix) {
      actions.updatePreviousNamePrefix(namePrefix);
    }
  }, [tenantsListFetching, namePrefix, previousNamePrefix, actions]);

  useEffect(() => {
    if (!tenantsListFetching) {
      const queriedAccounts: Array<TenantItem> = tenantsListData?.tenants ?? [];
      if (queriedAccounts.length > 0) {
        setAccounts(() => {
          const newAccounts: { [tenantId: string]: TenantItem } = {};
          queriedAccounts.forEach(queriedAccount => {
            newAccounts[queriedAccount.id] = queriedAccount;
          });
          return {
            ...newAccounts
          };
        });
      }
    }
  }, [tenantsListData, tenantsListFetching]);

  return (
    <TenantSwitcherRaw
      tenants={queriedAccounts}
      incrementFrom={actions.incrementFrom}
      namePrefix={namePrefix}
      updateNamePrefix={actions.updateNamePrefix}
      loading={tenantsListFetching}
    />
  );
};

interface DisplayTenant {
  id?: string;
  name: string;
  select?: TenantItem;
}

export const TenantSwitcherRaw: React.FC<TenantSwitcherProps> = props => {
  const {
    loading,
    incrementFrom,
    namePrefix,
    updateNamePrefix,
    tenants
  } = props;
  const { tenant } = useContext(SelectedTenantContext);
  const [open, setOpen] = useState(false);
  const self = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(namePrefix ?? "");
  const placeholder = unknownNamePlaceholder(t);
  const isDisabled = useLocation().pathname.startsWith("/admin/accounts");
  const [activeTenant, setActive, clearTenant] = useQueryParam<SelectedTenant>(
    "tenantId"
  );
  const hasPermission = usePermissions();
  const [setUserMeta] = useUserMeta();
  let displayValue = t("ALL_ACCOUNTS");

  if (tenant) displayValue = tenant.name || placeholder;

  const displayTenants: Array<DisplayTenant> = tenants.map(t => ({
    id: t.id,
    name: t.name || placeholder,
    select: t
  }));

  const isActiveTenant = (id: string | undefined) => {
    // If tenant is selected and matches the parameter, or
    // no tenant is selected and the tenant is void
    return activeTenant && activeTenant === id;
  };
  const scrollToTop = () =>
    document.getElementById("first-item")?.scrollIntoView();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!self?.current?.contains(e.target as Node)) {
        // Return to top of list upon close
        setOpen(false);
        scrollToTop();
      }
    };

    document.addEventListener("mousedown", handleClick, false);

    return () => {
      document.removeEventListener("mousedown", handleClick, false);
    };
  }, []);

  const ViewMore: React.FC = () => {
    if (displayTenants.length <= MAX_TENANT_ITEMS || !open) {
      return null;
    }

    return (
      <span
        onClick={() => {
          const lastAccountElement = document.getElementById("last-account");
          if (lastAccountElement) {
            lastAccountElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "center"
            });
          }
        }}
      >
        <PolyIcon icon="keyboard_arrow_down" />
      </span>
    );
  };

  const accountsLabel = hasPermission("READ_TENANT", "*")
    ? t("ACCOUNTS")
    : t("MANAGE_ACCOUNTS");

  const switcherToggleClasses = [
    "account-toggle",
    isDisabled && "disabled",
    open && "active"
  ]
    .filter(c => !!c)
    .join(" ");
  const tenantListWrapperClasses = [
    "menu vertical font-sm text-left account-switcher",
    open && "open"
  ]
    .filter(c => !!c)
    .join(" ");
  const tenantListClasses = [
    "pl-0 font-sm text-left account-switcher border bottom",
    displayTenants.length > MAX_TENANT_ITEMS && "scroll"
  ]
    .filter(c => !!c)
    .join(" ");

  const menuWrapClasses = ["account-switcher-menu-wrap", open && "open"]
    .filter(c => !!c)
    .join(" ");

  return (
    <div ref={self} className="account-switcher-wrap">
      <span
        onClick={() => {
          if (!isDisabled) {
            // Return to top of list upon close
            if (open) {
              scrollToTop();
            }
            setOpen(!open);
          }
        }}
        className={switcherToggleClasses}
        data-testid="accounts-trigger"
        role="button"
        aria-controls="account-menu"
        id="account-toggle"
        aria-haspopup="true"
        aria-label="Account Menu"
        aria-expanded={open ? "true" : "false"}
      >
        <p>
          <span className="light-gray">{t("ACCOUNT")}</span>
          {displayValue}
        </p>
        <PolyIcon
          icon="keyboard_arrow_down"
          className={`${open ? "active" : ""}`}
          title={t("OPEN_ACCOUNTS")}
        />
      </span>
      <div className={menuWrapClasses}>
        <div
          className={tenantListWrapperClasses}
          aria-labelledby="account-toggle"
          data-testid="tenant-menu"
        >
          <div className="account-search border bottom">
            <PolyIcon
              icon="search"
              className="account-search-icon white"
              data-testid="account-search-icon"
            />
            <input
              type="text"
              maxLength={15}
              placeholder={t("SEARCH")}
              value={searchTerm}
              data-testid="search-input"
              id="first-item"
              onChange={e => {
                updateNamePrefix(e.target.value);
                setSearchTerm(e.target.value);
              }}
            />
            {namePrefix && (
              <span
                data-testid="search-clear"
                title="search-clear"
                className="clear-wrap"
                onClick={() => {
                  updateNamePrefix("");
                  setSearchTerm("");
                }}
              >
                <PolyIcon className="white" icon="clear_x" />
              </span>
            )}
          </div>
          <div className={tenantListClasses}>
            <ul className="pl-0" id="account-menu">
              {displayTenants.map((tenant, index) => (
                <TenantRow
                  key={tenant.id}
                  index={index}
                  tenant={tenant}
                  displayTenants={displayTenants}
                  isActiveTenant={isActiveTenant}
                  setActive={setActive}
                  updateNamePrefix={updateNamePrefix}
                  setSearchTerm={setSearchTerm}
                  namePrefix={namePrefix}
                  clearTenant={clearTenant}
                  setOpen={setOpen}
                  setUserMeta={setUserMeta}
                  listLength={displayTenants.length}
                  incrementFrom={incrementFrom}
                />
              ))}
            </ul>
            {loading && <LoadingIndicator />}
            <ViewMore />
          </div>
          <div className="border top">
            <Link
              to="/admin/accounts"
              onClick={() => {
                trackMenuSelection(accountsLabel);
                setOpen(false);
              }}
            >
              <PolyIcon icon="manage_accounts" className="white mr-5" />
              {accountsLabel}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const TenantRow: React.FC<{
  tenant: DisplayTenant;
  index: number;
  isActiveTenant: (id: string | undefined) => boolean | "" | 0 | null;
  setOpen: Dispatch<SetStateAction<boolean>>;
  displayTenants: Array<DisplayTenant>;
  incrementFrom: PayloadAC<"IncrementFrom", number>;
  listLength: number;
  setActive: (paramsObj: QParam) => void;
  clearTenant: () => void;
  setUserMeta: SetUserMetaFn;
  updateNamePrefix: PayloadAC<"UpdateNamePrefix", string | undefined>;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  namePrefix: string | undefined;
}> = ({
  tenant,
  index,
  isActiveTenant,
  setActive,
  clearTenant,
  setOpen,
  displayTenants,
  setUserMeta,
  incrementFrom,
  listLength,
  updateNamePrefix,
  setSearchTerm,
  namePrefix
}) => {
  const isLastItem = index === listLength - 1;
  const onChange = (isVisible: boolean) => {
    if (isVisible) incrementFrom(accountLimit);
  };
  const history = useHistory();
  const isUserOnPolicyDetails = history.location.pathname.includes(
    "/manage/policy/details"
  );
  const isUserOnPolicyList = history.location.pathname.includes(
    "/manage/policy-test/list"
  );
  const [siteId] = useQueryParamString("site");
  const [groupId] = useQueryParamString("group");
  const [, , clearFilter] = usePolicyFilterValue();

  const TenantListItem = (
    <li
      key={`tenant-${tenant.id}-${index}`}
      id={index === displayTenants.length - 1 ? "last-account" : ""}
      className={`tenant button ${isActiveTenant(tenant.id) ? "active" : ""}`}
      onClick={() => {
        if (tenant.id) {
          setActive(tenant.id);
          setUserMeta({ lastTenantId: tenant.id });
          if (isUserOnPolicyDetails) {
            const scope = siteId ? "site" : groupId ? "group" : "model";
            history.push(`/manage/policy/list/${scope}`);
          }
          if (isUserOnPolicyList) {
            clearFilter(true);
          }
        } else {
          clearTenant();
        }
        removeBetaNotificationFromLocalStorage();
        if (namePrefix !== undefined) {
          updateNamePrefix("");
          setSearchTerm("");
        }
        setOpen(false);
      }}
      data-testid="tenant-item"
    >
      {tenant.name}
    </li>
  );

  return isLastItem ? (
    <VisibilitySensor key={tenant.id} onChange={onChange} partialVisibility>
      {TenantListItem}
    </VisibilitySensor>
  ) : (
    TenantListItem
  );
};

export default TenantSwitcher;
